import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { fetchVisualizationsForLiveboard, fetchVisualizationsWithSearch } from '../services/api';

interface VizSearchResult {
  id: string;
  name: string;
  type: string;
  description: string;
}

interface UseVizSearchReturn {
  data: VizSearchResult[];
  loading: boolean;
  error: string | null;
}

export const useVizSearch = (
  liveboardId: string,
  searchTerm: string,
  debounceMs = 300
): UseVizSearchReturn => {
  const [data, setData] = useState<VizSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useCallback(
    _.debounce(async (lbId: string, term: string) => {
      if (!lbId) {
        setData([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let result;
        if (!term.trim()) {
          // Fetch all visualizations for the liveboard when no search term
          result = await fetchVisualizationsForLiveboard(lbId);
          console.error('result', result);
        } else {
          // Search for specific visualizations within the liveboard
          result = await fetchVisualizationsWithSearch(lbId, term);
        }

        const transformedData = result?.objects || result || [];
        setData(transformedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch visualizations');
        setData([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [debounceMs]
  );

  useEffect(() => {
    debouncedSearch(liveboardId, searchTerm);

    return () => {
      debouncedSearch.cancel();
    };
  }, [liveboardId, searchTerm, debouncedSearch]);

  // Reset when liveboard changes
  useEffect(() => {
    if (liveboardId) {
      setData([]);
      setError(null);
      // Don't set loading here - let debouncedSearch handle it
    } else {
      setData([]);
      setError(null);
      setLoading(false);
    }
  }, [liveboardId, searchTerm, debouncedSearch]);

  return {
    data,
    loading,
    error,
  };
};
